import { useEffect, useState } from "react";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { PageHeader, NBCard, NBInput, NBButton, NBTextarea, Avatar } from "../components/UI";
import { toast } from "sonner";
import { Save, Lock, Star, ShieldCheck, KeyRound, Copy, Check, Download, Package, Bell, Archive, Mail, Share2, Trash2, Link as LinkIcon } from "lucide-react";
import { shareOrCopy, canNativeShare } from "../lib/share";

const COLORS = ["#E8B96B", "#B8CFA9", "#E9B5A0", "#A8BFD1", "#C28F4E", "#C75D3F"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: "", dob: "", phone: "", bio: "", avatar_color: "#E8B96B" });
  const [saving, setSaving] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [points, setPoints] = useState(0);

  useEffect(() => {
    if (user?.member) {
      setForm({
        name: user.member.name || "",
        dob: user.member.dob || "",
        phone: user.member.phone || "",
        bio: user.member.bio || "",
        avatar_color: user.member.avatar_color || "#E8B96B",
      });
    }
    api.get("/api/rewards").then(({ data }) => {
      const mine = data.find((r) => r.member_id === user?.member?.id);
      setPoints(mine?.points || 0);
    }).catch(() => {});
  }, [user]);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/api/auth/profile", form);
      toast.success("Profile updated!");
      await refresh();
    } catch {
      toast.error("Failed to update");
    }
    setSaving(false);
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (pwForm.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/api/auth/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed!");
      setPwForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    }
    setPwSaving(false);
  };

  const member = user?.member || {};
  const isAccount = member.is_account;
  const phoneIsE164 = (form.phone || "").trim().startsWith("+");
  const twoFAEnabled = !!member.two_factor_enabled;
  const [backupStatus, setBackupStatus] = useState({ has_codes: false, total: 0, used: 0, remaining: 0 });
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [codesCopied, setCodesCopied] = useState(false);

  useEffect(() => {
    if (twoFAEnabled) {
      api.get("/api/auth/backup-codes/status").then(({ data }) => setBackupStatus(data)).catch(() => {});
    }
  }, [twoFAEnabled]);

  const generateBackupCodes = async () => {
    if (backupStatus.has_codes && !window.confirm("This will replace your existing backup codes. Continue?")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/api/auth/backup-codes/generate");
      setGeneratedCodes(data.codes);
      const status = await api.get("/api/auth/backup-codes/status");
      setBackupStatus(status.data);
      toast.success("Backup codes generated — save them now!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setGenerating(false);
  };

  const copyCodes = () => {
    if (!generatedCodes) return;
    navigator.clipboard.writeText(generatedCodes.join("\n"));
    setCodesCopied(true);
    toast.success("Codes copied!");
    setTimeout(() => setCodesCopied(false), 2000);
  };

  const toggle2FA = async () => {
    try {
      await api.patch("/api/auth/profile", { two_factor_enabled: !twoFAEnabled });
      toast.success(twoFAEnabled ? "Two-factor disabled" : "Two-factor enabled — you'll receive an SMS code at sign-in");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to toggle 2FA");
    }
  };

  const [exporting, setExporting] = useState(false);

  // Family settings (admin only): weekly briefing, daily digest, auto-backup
  const family = user?.family || {};
  const isAdmin = member.role === "admin" || member.role === "Admin" || member.is_admin;
  const [settings, setSettings] = useState({
    weekly_email_enabled: false,
    daily_email_enabled: false,
    auto_backup_enabled: false,
    auto_share_digest_in_email: false,
  });
  const [settingsSaving, setSettingsSaving] = useState(null);

  useEffect(() => {
    if (family) {
      setSettings({
        weekly_email_enabled: !!family.weekly_email_enabled,
        daily_email_enabled: !!family.daily_email_enabled,
        auto_backup_enabled: !!family.auto_backup_enabled,
        auto_share_digest_in_email: !!family.auto_share_digest_in_email,
      });
    }
  }, [family?.id, family?.weekly_email_enabled, family?.daily_email_enabled, family?.auto_backup_enabled, family?.auto_share_digest_in_email]);

  const toggleSetting = async (key, label) => {
    const next = !settings[key];
    setSettings((s) => ({ ...s, [key]: next }));
    setSettingsSaving(key);
    try {
      await api.patch("/api/family/settings", { [key]: next });
      toast.success(`${label} ${next ? "enabled" : "disabled"}`);
      await refresh();
    } catch (err) {
      // revert
      setSettings((s) => ({ ...s, [key]: !next }));
      toast.error(err.response?.data?.detail || "Failed to update");
    }
    setSettingsSaving(null);
  };

  // Share links (admin)
  const [shareLinks, setShareLinks] = useState([]);
  const [shareDays, setShareDays] = useState(30);
  const [shareCreating, setShareCreating] = useState(false);
  const [copiedLinkId, setCopiedLinkId] = useState(null);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/api/family/share-links").then(({ data }) => setShareLinks(data || [])).catch(() => {});
  }, [isAdmin]);

  const buildPublicUrl = (token) => `${window.location.origin}/digest/${token}`;

  const createShareLink = async () => {
    setShareCreating(true);
    try {
      const { data } = await api.post("/api/family/share-link", { days_valid: Number(shareDays) || 30 });
      const url = buildPublicUrl(data.token);
      await navigator.clipboard.writeText(url).catch(() => {});
      toast.success("Share link copied to clipboard");
      // Refetch list
      const { data: list } = await api.get("/api/family/share-links");
      setShareLinks(list || []);
      // Stash the freshly minted token so user can copy again from the row
      setShareLinks((prev) => prev.map((l) => l.id === data.id ? { ...l, _token: data.token } : l));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create share link");
    }
    setShareCreating(false);
  };

  const revokeShareLink = async (linkId) => {
    if (!window.confirm("Revoke this share link? Anyone using it will lose access immediately.")) return;
    try {
      await api.delete(`/api/family/share-link/${linkId}`);
      setShareLinks((prev) => prev.map((l) => l.id === linkId ? { ...l, revoked: true } : l));
      toast.success("Link revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyShareLink = async (link) => {
    if (!link._token) {
      toast.info("Token only available right after minting. Create a new link to share it.");
      return;
    }
    await navigator.clipboard.writeText(buildPublicUrl(link._token));
    setCopiedLinkId(link.id);
    toast.success("Copied!");
    setTimeout(() => setCopiedLinkId(null), 1500);
  };

  // Topic publishes (school/daycare-style — single-section, mailed to a partner)
  const [topicPubs, setTopicPubs] = useState([]);
  const [topicForm, setTopicForm] = useState({ email: "", section: "calendar", days_valid: 90, note: "" });
  const [topicCreating, setTopicCreating] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/api/family/topic-publishes").then(({ data }) => setTopicPubs(data || [])).catch(() => {});
  }, [isAdmin]);

  const submitTopicPublish = async (e) => {
    e.preventDefault();
    setTopicCreating(true);
    try {
      await api.post("/api/family/topic-publish", {
        email: topicForm.email,
        section: topicForm.section,
        days_valid: Number(topicForm.days_valid) || 90,
        note: topicForm.note,
        app_url: window.location.origin,
      });
      toast.success(`Sent to ${topicForm.email} 📬`);
      setTopicForm({ email: "", section: "calendar", days_valid: 90, note: "" });
      const { data } = await api.get("/api/family/topic-publishes");
      setTopicPubs(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
    setTopicCreating(false);
  };

  const revokeTopicPub = async (id) => {
    if (!window.confirm("Revoke this share?")) return;
    await api.delete(`/api/family/topic-publish/${id}`);
    setTopicPubs((prev) => prev.map((p) => p.id === id ? { ...p, revoked: true } : p));
    toast.success("Revoked");
  };
  const exportFamily = async () => {
    setExporting(true);
    try {
      const response = await api.get("/api/family/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      // Try to get filename from Content-Disposition
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      link.download = match ? match[1] : "familyos-export.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded!");
    } catch {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  const [exportingPdf, setExportingPdf] = useState(false);
  const exportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await api.get("/api/family/export-pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/);
      link.download = match ? match[1] : "familytable.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded!");
    } catch {
      toast.error("PDF export failed");
    }
    setExportingPdf(false);
  };

  return (
    <div data-testid="profile-page">
      <PageHeader
        title="My Profile"
        subtitle="Manage your personal info, avatar, and account."
        color="#B8CFA9"
      />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Profile card preview */}
        <NBCard color={form.avatar_color} className="lg:col-span-1" hover={false}>
          <div className="flex flex-col items-center text-center py-4">
            <Avatar name={form.name} color="white" size={88} />
            <h2 className="text-3xl font-black mt-4" style={{ fontFamily: "var(--hh-display)" }}>{form.name || "Your Name"}</h2>
            <span className="nb-badge bg-white mt-2">{member.role}</span>
            {isAccount && <span className="nb-badge bg-white mt-2 ml-2">✓ Active Account</span>}
            <div className="flex items-center gap-2 mt-4 nb-card !p-3 !shadow-none bg-white" style={{ border: "2px solid #0A0A0A" }}>
              <Star strokeWidth={3} className="w-5 h-5 fill-[#E8B96B]" />
              <span className="font-black text-2xl" style={{ fontFamily: "var(--hh-display)" }} data-testid="profile-points">{points}</span>
              <span className="text-xs font-bold uppercase tracking-wider">Reward Points</span>
            </div>
            {form.bio && <p className="mt-4 text-sm italic">"{form.bio}"</p>}
          </div>
        </NBCard>

        {/* Edit form */}
        <NBCard className="lg:col-span-2" hover={false}>
          <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>Personal Info</h3>
          <form onSubmit={saveProfile} className="space-y-3">
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">Display Name</label>
              <NBInput testId="profile-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Date of Birth</label>
                <NBInput testId="profile-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Phone</label>
                <NBInput testId="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-1 block">Bio</label>
              <NBTextarea testId="profile-bio" placeholder="Something about you..." value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-black uppercase tracking-wider mb-2 block">Avatar Color</label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map((c) => (
                  <button
                    type="button"
                    key={c}
                    onClick={() => setForm({ ...form, avatar_color: c })}
                    data-testid={`profile-color-${c}`}
                    className={`w-12 h-12 rounded-full border-2 border-black ${form.avatar_color === c ? "nb-shadow-lg" : ""}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
            <NBButton testId="save-profile" type="submit" disabled={saving} color="#B8CFA9" className="w-full">
              <Save strokeWidth={3} className="w-4 h-4 inline mr-1" />
              {saving ? "Saving..." : "Save Profile"}
            </NBButton>
          </form>
        </NBCard>

        {/* Account info */}
        {isAccount && (
          <NBCard className="lg:col-span-3" hover={false}>
            <h3 className="text-2xl font-black mb-4 flex items-center gap-2" style={{ fontFamily: "var(--hh-display)" }}>
              <Lock strokeWidth={3} className="w-6 h-6" /> Change Password
            </h3>
            <form onSubmit={changePassword} className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Current Password</label>
                <NBInput testId="pw-current" type="password" required value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">New Password</label>
                <NBInput testId="pw-new" type="password" required minLength={6} value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Confirm</label>
                <NBInput testId="pw-confirm" type="password" required minLength={6} value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
              </div>
              <div className="sm:col-span-3">
                <NBButton testId="save-password" type="submit" disabled={pwSaving} color="#E9B5A0">
                  {pwSaving ? "Changing..." : "Change Password"}
                </NBButton>
              </div>
            </form>
            <div className="mt-4 pt-4 border-t-2 border-dashed border-neutral-300 text-sm space-y-1">
              <p><span className="font-bold">Email:</span> {member.email}</p>
              <p><span className="font-bold">Role:</span> {member.role}</p>
              <p><span className="font-bold">Joined:</span> {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : "—"}</p>
            </div>
          </NBCard>
        )}

        {isAccount && (
          <NBCard color={twoFAEnabled ? "#B8CFA9" : "white"} className="lg:col-span-3" hover={false} data-testid="twofa-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl border-2 border-black bg-white flex items-center justify-center shrink-0">
                  <ShieldCheck strokeWidth={3} />
                </div>
                <div>
                  <h3 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>
                    Two-Factor Authentication
                    {twoFAEnabled && <span className="nb-badge bg-white ml-2 align-middle">ON</span>}
                  </h3>
                  <p className="text-sm mt-1 max-w-xl">When enabled, signing in requires a 6-digit code texted to your phone. Adds a real security layer for documents, photos, and budget data.</p>
                  {!phoneIsE164 && !twoFAEnabled && (
                    <p className="text-xs font-bold mt-2 bg-[#E9B5A0] inline-block border-2 border-black rounded-full px-3 py-1">⚠ Save your phone in E.164 format above first (e.g. +15551234567)</p>
                  )}
                  {twoFAEnabled && member.phone && (
                    <p className="text-xs font-bold mt-2">📱 Codes sent to {member.phone.slice(0, -4).replace("+", "+•") + member.phone.slice(-4)}</p>
                  )}
                </div>
              </div>
              <button
                onClick={toggle2FA}
                disabled={!twoFAEnabled && !phoneIsE164}
                data-testid="toggle-2fa"
                className={`nb-btn px-5 py-2.5 text-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed ${twoFAEnabled ? "bg-[#E9B5A0]" : "bg-[#E8B96B]"}`}
              >
                {twoFAEnabled ? "Disable 2FA" : "Enable 2FA"}
              </button>
            </div>
          </NBCard>
        )}

        {isAccount && twoFAEnabled && (
          <NBCard className="lg:col-span-3" hover={false} data-testid="backup-codes-card">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl border-2 border-black bg-[#E8B96B] flex items-center justify-center shrink-0">
                <KeyRound strokeWidth={3} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Backup Codes</h3>
                <p className="text-sm mt-1">Single-use recovery codes for when you can't access your phone. Store them somewhere safe (password manager, printed copy).</p>
                {backupStatus.has_codes && (
                  <p className="text-xs font-bold mt-2">
                    <span className="nb-badge bg-[#B8CFA9]">{backupStatus.remaining} remaining</span>
                    {backupStatus.used > 0 && <span className="nb-badge bg-white ml-1">{backupStatus.used} used</span>}
                  </p>
                )}
              </div>
              <button onClick={generateBackupCodes} disabled={generating} data-testid="generate-backup-codes" className="nb-btn bg-[#E8B96B] px-4 py-2 text-sm shrink-0 disabled:opacity-50">
                {generating ? "Generating..." : backupStatus.has_codes ? "Regenerate" : "Generate"}
              </button>
            </div>
            {generatedCodes && (
              <div className="mt-4 nb-card !p-4 !shadow-none bg-[#F6EFE4]" style={{ border: "2px dashed #0A0A0A" }} data-testid="generated-codes">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-black uppercase tracking-wider">⚠ Save these now — they won't be shown again</p>
                  <button onClick={copyCodes} className="nb-btn bg-white px-3 py-1.5 text-xs">
                    {codesCopied ? <><Check strokeWidth={3} className="w-3 h-3 inline mr-1" />Copied</> : <><Copy strokeWidth={3} className="w-3 h-3 inline mr-1" />Copy all</>}
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {generatedCodes.map((c, i) => (
                    <div key={i} className="bg-white border-2 border-black rounded-lg p-2 text-center font-mono font-bold text-sm">{c}</div>
                  ))}
                </div>
              </div>
            )}
          </NBCard>
        )}

        {isAdmin && (
          <NBCard className="lg:col-span-3" hover={false} data-testid="family-notifications-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl border-2 border-black bg-[#E8B96B] flex items-center justify-center shrink-0">
                <Bell strokeWidth={3} />
              </div>
              <div>
                <h3 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Family Notifications &amp; Backup</h3>
                <p className="text-sm mt-1 max-w-2xl">Choose what your family receives by email. Admins only.</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                { key: "weekly_email_enabled", icon: Mail, color: "#B8CFA9", title: "Weekly briefing", desc: "AI-curated week ahead, delivered Sunday morning at your local briefing hour." },
                { key: "auto_share_digest_in_email", icon: Share2, color: "#E9B5A0", title: "Include public digest", desc: "Adds a 'see live page →' link to the weekly email — for sharing with grandparents." },
                { key: "daily_email_enabled", icon: Bell, color: "#E8B96B", title: "Daily digest", desc: "A short morning summary of today's events, tasks, and weather." },
                { key: "auto_backup_enabled", icon: Archive, color: "#E9B5A0", title: "Monthly auto-backup", desc: "We email a full ZIP of your family data to the admin on the 1st of every month." },
              ].map(({ key, icon: Icon, color, title, desc }) => {
                const on = !!settings[key];
                return (
                  <div
                    key={key}
                    className="nb-card !p-4 !shadow-none flex flex-col gap-3"
                    style={{ border: "2px solid #0A0A0A", background: on ? color : "white" }}
                    data-testid={`setting-${key}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="w-10 h-10 rounded-lg border-2 border-black bg-white flex items-center justify-center">
                        <Icon strokeWidth={3} className="w-5 h-5" />
                      </div>
                      <button
                        onClick={() => toggleSetting(key, title)}
                        disabled={settingsSaving === key}
                        data-testid={`toggle-${key}`}
                        className="nb-btn px-3 py-1 text-xs bg-white disabled:opacity-50"
                      >
                        {settingsSaving === key ? "…" : on ? "ON" : "OFF"}
                      </button>
                    </div>
                    <div>
                      <h4 className="font-black text-base" style={{ fontFamily: "var(--hh-display)" }}>{title}</h4>
                      <p className="text-xs mt-1 leading-relaxed">{desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </NBCard>
        )}

        {isAdmin && (
          <NBCard className="lg:col-span-3" hover={false} data-testid="share-links-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                   style={{ background: "var(--hh-peach)", color: "var(--hh-ink)" }}>
                <Share2 strokeWidth={2.4} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Public share links</h3>
                <p className="text-sm mt-1 max-w-2xl">Mint a read-only link grandparents or extended family can open with no login — shows members, this week's events, recent journal, recipes, and photos.</p>
              </div>
            </div>

            <div className="flex flex-wrap items-end gap-3 mb-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Valid for</label>
                <select value={shareDays} onChange={(e) => setShareDays(e.target.value)} data-testid="share-days-select" className="nb-input">
                  <option value={7}>7 days</option>
                  <option value={30}>30 days</option>
                  <option value={90}>90 days</option>
                  <option value={365}>1 year</option>
                </select>
              </div>
              <NBButton testId="create-share-link-btn" color="#E9B5A0" onClick={createShareLink} disabled={shareCreating}>
                <LinkIcon strokeWidth={2.4} className="w-4 h-4 inline mr-1" />
                {shareCreating ? "Creating…" : "Create & copy link"}
              </NBButton>
            </div>

            {shareLinks.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>No share links yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="share-links-list">
                {shareLinks.map((l) => {
                  const expired = new Date(l.expires_at) < new Date();
                  const status = l.revoked ? "Revoked" : expired ? "Expired" : "Active";
                  const statusColor = l.revoked || expired ? "#5C4A3A" : "#7E9F6B";
                  return (
                    <li key={l.id} className="flex items-center gap-3 p-3 rounded-2xl"
                        style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)", opacity: l.revoked ? 0.55 : 1 }}
                        data-testid={`share-link-${l.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold flex items-center gap-2">
                          <span className="px-2 py-0.5 text-[10px] rounded-full font-semibold" style={{ background: "white", border: "1px solid var(--hh-line)", color: statusColor }}>
                            {status}
                          </span>
                          <span style={{ color: "var(--hh-ink-soft)" }}>{l.view_count || 0} views</span>
                        </div>
                        <div className="text-xs mt-1" style={{ color: "var(--hh-ink-soft)" }}>
                          Created {new Date(l.created_at).toLocaleDateString()} · expires {new Date(l.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      {!l.revoked && !expired && (
                        <>
                          {l._token && (
                            <button onClick={() => copyShareLink(l)} className="nb-btn px-3 py-1.5 text-xs" data-testid={`copy-share-${l.id}`} title="Copy link">
                              {copiedLinkId === l.id ? <Check strokeWidth={2.4} className="w-4 h-4" /> : <Copy strokeWidth={2.4} className="w-4 h-4" />}
                            </button>
                          )}
                          {l._token && canNativeShare() && (
                            <button onClick={() => nativeShareLink(l)} className="nb-btn px-3 py-1.5 text-xs" data-testid={`share-link-native-${l.id}`} title="Share">
                              <Share2 strokeWidth={2.4} className="w-4 h-4" />
                            </button>
                          )}
                          <button onClick={() => revokeShareLink(l.id)} className="nb-btn px-3 py-1.5 text-xs" data-testid={`revoke-share-${l.id}`} title="Revoke">
                            <Trash2 strokeWidth={2.4} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </NBCard>
        )}


        {isAdmin && (
          <NBCard className="lg:col-span-3" hover={false} data-testid="topic-publish-card">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0"
                   style={{ background: "var(--hh-dusk-soft)", color: "var(--hh-ink)" }}>
                <Mail strokeWidth={2.4} />
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-extrabold" style={{ fontFamily: "var(--hh-display)" }}>Share a section by email</h3>
                <p className="text-sm mt-1 max-w-2xl">
                  School, daycare, or a coach? Mail them a read-only link to a single section — calendar, contacts, documents, or recipes.
                </p>
              </div>
            </div>

            <form onSubmit={submitTopicPublish} className="grid sm:grid-cols-12 gap-3 mb-5" data-testid="topic-publish-form">
              <input type="email" required placeholder="recipient@school.edu" value={topicForm.email}
                onChange={(e) => setTopicForm({ ...topicForm, email: e.target.value })}
                className="nb-input sm:col-span-5" data-testid="topic-email" />
              <select value={topicForm.section} onChange={(e) => setTopicForm({ ...topicForm, section: e.target.value })}
                className="nb-input sm:col-span-3" data-testid="topic-section">
                <option value="calendar">Calendar</option>
                <option value="contacts">Contacts</option>
                <option value="documents">Documents</option>
                <option value="recipes">Recipes</option>
              </select>
              <select value={topicForm.days_valid} onChange={(e) => setTopicForm({ ...topicForm, days_valid: e.target.value })}
                className="nb-input sm:col-span-2" data-testid="topic-days">
                <option value={30}>30 days</option>
                <option value={90}>90 days</option>
                <option value={180}>180 days</option>
                <option value={365}>1 year</option>
              </select>
              <NBButton testId="topic-submit" type="submit" color="#A8BFD1" disabled={topicCreating} className="sm:col-span-2">
                {topicCreating ? "Sending…" : "Send"}
              </NBButton>
              <input type="text" placeholder="Optional note for the recipient" value={topicForm.note}
                onChange={(e) => setTopicForm({ ...topicForm, note: e.target.value })}
                className="nb-input sm:col-span-12" data-testid="topic-note" />
            </form>

            {topicPubs.length === 0 ? (
              <p className="text-sm" style={{ color: "var(--hh-ink-soft)" }}>No section shares yet.</p>
            ) : (
              <ul className="space-y-2" data-testid="topic-publishes-list">
                {topicPubs.map((p) => {
                  const expired = new Date(p.expires_at) < new Date();
                  const status = p.revoked ? "Revoked" : expired ? "Expired" : "Active";
                  return (
                    <li key={p.id} className="flex items-center gap-3 p-3 rounded-2xl"
                      style={{ background: "var(--hh-card-soft)", border: "1px solid var(--hh-line)", opacity: p.revoked ? 0.55 : 1 }}
                      data-testid={`topic-pub-${p.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {p.section} → <span style={{ color: "var(--hh-ink-soft)" }}>{p.email}</span>
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "var(--hh-ink-soft)" }}>
                          {status} · {p.view_count || 0} views · expires {new Date(p.expires_at).toLocaleDateString()}
                        </div>
                      </div>
                      {!p.revoked && !expired && (
                        <button onClick={() => revokeTopicPub(p.id)} className="nb-btn p-1.5" data-testid={`revoke-topic-${p.id}`}>
                          <Trash2 strokeWidth={2.4} className="w-4 h-4" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </NBCard>
        )}



        <NBCard color="#B8CFA9" className="lg:col-span-3" hover={false} data-testid="export-card">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-xl border-2 border-black bg-white flex items-center justify-center shrink-0">
                <Package strokeWidth={3} />
              </div>
              <div>
                <h3 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>Export Family Data</h3>
                <p className="text-sm mt-1 max-w-xl">Download <strong>everything</strong> — events, tasks, recipes, photos, documents, chat history, budget, goals — as a single ZIP. Your data, anytime, no lock-in.</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <button
                onClick={exportPdf}
                disabled={exportingPdf}
                data-testid="export-pdf"
                className="nb-btn px-5 py-2.5 text-sm bg-white disabled:opacity-50"
              >
                <Download strokeWidth={2.4} className={`w-4 h-4 inline mr-1 ${exportingPdf ? "animate-bounce" : ""}`} />
                {exportingPdf ? "Building PDF…" : "Download .pdf"}
              </button>
              <button
                onClick={exportFamily}
                disabled={exporting}
                data-testid="export-family"
                className="nb-btn px-5 py-2.5 text-sm bg-white disabled:opacity-50"
              >
                <Download strokeWidth={2.4} className={`w-4 h-4 inline mr-1 ${exporting ? "animate-bounce" : ""}`} />
                {exporting ? "Building ZIP…" : "Download .zip"}
              </button>
            </div>
          </div>
        </NBCard>
      </div>
    </div>
  );
}
